<template>
  <div class="factsheet-page-component">
    <div class="dropdown-items">
      <ChipGroup :options="selectedSources" v-model="selectedOptions" />

      <div class="filter-status">
        <!-- ML fill mode + threshold + diff view -->
        <div class="ml-controls">
          <label class="ml-radio">
            <input type="radio" value="analysis_only" v-model="includeMlMode" />
            Analysis only
          </label>

          <label class="ml-radio" :title="ml_predictions ? '' : 'No ML data available'">
            <input type="radio" value="analysis_plus_ml" v-model="includeMlMode" :disabled="!hasMlData" />
            Fill gaps with ML
          </label>

          <label class="ml-radio" :title="hasMlData
            ? 'Show Analysis − ML difference'
            : 'No ML data available'
            ">
            <input type="radio" value="diff_analysis_minus_ml" v-model="includeMlMode" :disabled="!hasMlData" />
            Analysis − ML
          </label>

          <label v-if="includeMlMode !== 'analysis_only'" class="ml-threshold"
            :title="'Drop ML predictions below this level'">
            Min ML dBA:
            <input type="number" step="0.1" min="-60" max="150" v-model.number="mlMinDb" class="num-input" />
          </label>

          <span v-if="includeMlMode === 'analysis_plus_ml'" class="pill pill-on">
            ML fill active (≥ {{ mlMinDb.toFixed(1) }} dBA)
          </span>

          <span v-if="includeMlMode === 'diff_analysis_minus_ml'" class="pill pill-on">
            Showing difference: Analysis − ML (≥ {{ mlMinDb.toFixed(1) }} dBA)
          </span>

          <label v-if="includeMlMode === 'diff_analysis_minus_ml'" class="ml-threshold"
            :title="'Force y-axis to be centered at 0 for differences'">
            <input type="checkbox" v-model="zeroCenterDiff" />
            Zero-center axis
          </label>

        </div>
          <span v-if="filterInUse" class="pill pill-on">
            Filtering {{ selectedOptions.length }} source(s) across
            {{ selectedTimesCount }} × 10-s timesteps
          </span>
          <span v-else class="pill pill-off">
            No matching timestamps for current source selections — using full
            buffer.
          </span>
      </div>
    </div>

    <div class="dashboard">
      <div class="left-column">
        <div class="component-container aggregates">
          <h1 class="aggregates-chart-title">{{ chartTitle }}</h1>
          <div class="aggregates-chart-wrapper">
            <div ref="aggregatesChart" class="aggregates-chart"></div>
            <div v-if="contributionsLoading" class="aggregates-spinner-overlay">
      <img src="@/assets/svg/12-dots-scale-rotate-magecdn.svg" alt="Loading..." class="aggregates-spinner" />
            </div>
          </div>
        </div>
        <WeightedSpectra :weightedSpectra="weightedSpectra" :APIparams="filtered_API_params"
          ref="weighted_spectra_ref" />
      </div>
      <div class="right-column">
        <div class="component-container">
          <TonalNoise :APIparams="filtered_API_params" :regions="assessmentRegions" :sourceNames="selectedOptions.filter(s => s !== 'All')" ref="tonal_noise_ref" />
        </div>
        <div class="component-container">
          <LowFrequencyNoise :APIparams="filtered_API_params" :regions="assessmentRegions" :sourceNames="selectedOptions.filter(s => s !== 'All')" ref="lfn_ref" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import WeightedSpectra from "./WeightedSpectra.vue";
import TonalNoise from "./TonalNoise.vue";
import LowFrequencyNoise from "./LowFrequencyNoise.vue";
import ChipGroup from "./ChipGroup.vue";
import * as echarts from "echarts";
import axios from "axios";
import { api_config } from "@/env.ts";
import { getToken } from "../keycloak-wrapper.ts";
import { bankersRound, buildAssessmentRegions } from "../helpers.ts";

export default {
  name: "FactSheetCDashboard",
  components: {
    WeightedSpectra,
    TonalNoise,
    LowFrequencyNoise,
    ChipGroup,
  },
  props: {
    APIparams: { type: Object, required: true },
    weightedSpectra: { type: Object, required: true },
    selectable_sources: { type: Array, required: true },
    formulaMap: { type: Object, required: true },
    selectedSources: { type: Array, default: () => [] },

    sourcesMap: {type: Object, default: () => ({})},
    regionMap: {type: Object, default: () => ({})},

    /**
     * ml_predictions structure (per-source, per-timestamp dBA):
     * {
     *   <sourceA>: { "YYYY-MM-DD HH:MM:SS": dBA, ... },
     *   <sourceB>: { "YYYY-MM-DD HH:MM:SS": dBA, ... },
     *   ...
     * }
     */
    ml_predictions: { type: Object, default: () => ({}) },
  },
  setup(props){
    // Formats the regions and sources together by their frequencies and
    // timestamps giving us masks over the spectra matrix for that source
    const format_sources_with_regions = (sources, regions) => {
      const sources_selections = Object.values(sources);
      const formatted_sources_with_regions = [];
      for(let i = 0; i < sources_selections.length; i++){
        const source_selection_array = sources_selections[i];
        const source_selection_regions = regions[i] || [];
          for(const source_selection of source_selection_array){
            const source_selection_object = new Object();
            source_selection_object.source_name = source_selection.name;
            source_selection_object.regions = [];
            for (const region_selection of source_selection_regions) {
              const region_selection_object = {
                // JS doesn't have a native way to handle these datetimes without local TZ conversion
                // So we'll just pass the strings to the backend and handle it there.
                start : region_selection[0],
                end : region_selection[1],
                formula : source_selection.formula
              };
              source_selection_object.regions.push(region_selection_object)
            }
            formatted_sources_with_regions.push(source_selection_object)
      }}
      return formatted_sources_with_regions;
    };

    // Returns a Z weighted spectra with the Z- prefix removed from the frequencies
    const format_weighted_spectra = (weightedSpectra) => {
      const formatted_weighted_spectra = {};
      const z_weighted_spectra = weightedSpectra.Z;
      for (const [frequency, value] of Object.entries(z_weighted_spectra)){
        const frequency_string = frequency.toString().replace("Z-", "");
        formatted_weighted_spectra[frequency_string] = value;
      }
      return formatted_weighted_spectra;
    }

    const formatted_source_regions = format_sources_with_regions(props.sourcesMap, props.regionMap);
    const formatted_weighted_spectra = format_weighted_spectra(props.weightedSpectra);
    return {
      formatted_source_regions,
      formatted_weighted_spectra,
    }
  },
  data() {
    return {
      // filtering
      selectedOptions: this.selectedSources,
      filtered_API_params: { ...this.APIparams },
      filterInUse: false,
      selectedTimesCount: 0,

      // reporting mode
      aggregationMode: "LAEQ_SELECTED",

      // timing assumptions
      sampleDurationSec: 10, // Δt per sample (s)

      // chart
      chart: null,

      // ML controls
      includeMlMode: "analysis_only", // "analysis_only" | "analysis_plus_ml" | "diff_analysis_minus_ml"
      mlMinDb: 20.0,
      zeroCenterDiff: true,

      sourceContributions: {},
      contributionsLoading: false,
    };
  },
  computed: {
    assessmentRegions() {
      const activeOptions = this.selectedOptions.filter(s => s !== "All");
      if (activeOptions.length === 0) return [];
      return buildAssessmentRegions(
        this.formatted_source_regions.filter(r => activeOptions.includes(r.source_name))
      );
    },
    hasMlData() {
      return this.ml_predictions && Object.keys(this.ml_predictions).length > 0;
    },
    chartTitle() {
      if (this.includeMlMode === "diff_analysis_minus_ml") {
        return "Analysis − ML noise level difference";
      }
      return "Noise source levels";
    },
  },
  methods: {

    applyModeToAPIParams() {
      this.filtered_API_params = {
        ...this.filtered_API_params,
        aggregationMode: this.aggregationMode,
      };
    },

    // On being made visible (i.e. Navigating from the main page to the fact
    // sheet), take the selections from the main page and apply them to this.
    apply_filters(filters){
      this.selectedOptions = filters;
      this.filterSelectedTimestamps();
    },

    async fetchSourceContributions() {
        this.contributionsLoading = true;
        try {
          const token = await getToken();
          const response = await axios.post(
            api_config.backend + "/sourceContributions",
            {
              weighted_spectra: this.formatted_weighted_spectra,
              source_regions: this.formatted_source_regions,
              tot_a: this.weightedSpectra.TOT_A ?? {},
            },
            { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
          );
          this.sourceContributions = response.data;
          this.updateAggregatesChart();
        } catch (e) {
          console.error("Error fetching source contributions:", e);
        } finally {
          this.contributionsLoading = false;
        }
    },

    filterSelectedTimestamps() {
      if (!this.formulaMap || !this.selectedOptions) return [];

      const aggregatedTimestamps = [];

      Object.keys(this.formulaMap).forEach((regionId) => {
        const regionData = this.formulaMap[regionId];
        if (!regionData || !regionData.sources_map || !regionData.regions)
          return;

        // Only include region intervals if at least one selected source exists there
        const regionSourcesMap = regionData.sources_map;
        const validSources = this.selectedOptions
          .filter((s) => s !== "All")
          .filter((source) => Object.hasOwn(regionSourcesMap, source));
        if (validSources.length === 0) return;

        regionData.regions.forEach((interval) => {
          const [startTimeStr, endTimeStr] = interval;
          const startTime = new Date(startTimeStr.replace(" ", "T"));
          const endTime = new Date(endTimeStr.replace(" ", "T"));
          if (startTime >= endTime) return;

          for (
            let t = new Date(startTime);
            t < endTime;
            t = new Date(t.getTime() + this.sampleDurationSec * 1000)
          ) {
            aggregatedTimestamps.push(this.formatDate(t));
          }
        });
      });

      const uniqueTimestamps = Array.from(new Set(aggregatedTimestamps)).sort();
      this.filtered_API_params.selected_times = uniqueTimestamps;
      this.selectedTimesCount = uniqueTimestamps.length;

      this.$refs.weighted_spectra_ref?.updateChart?.();
      this.filterInUse = uniqueTimestamps.length > 0;
    },

    formatDate(dateObj) {
      const pad = (n) => n.toString().padStart(2, "0");
      const y = dateObj.getFullYear();
      const m = pad(dateObj.getMonth() + 1);
      const d = pad(dateObj.getDate());
      const h = pad(dateObj.getHours());
      const mi = pad(dateObj.getMinutes());
      const s = pad(dateObj.getSeconds());
      return `${y}-${m}-${d} ${h}:${mi}:${s}`;
    },

    initAggregatesChart() {
      if (this.chart || !this.$refs.aggregatesChart) return;
      this.chart = echarts.init(this.$refs.aggregatesChart);
      window.addEventListener("resize", this.chart.resize);
    },

    updateAggregatesChart() {
      this.initAggregatesChart();
      if (!this.chart) return;

      const sourceCats = this.selectedOptions.filter((s) => s !== "All");
      const catsBase = ["All", ...sourceCats];

      const isDiff = this.includeMlMode === "diff_analysis_minus_ml";

      const laeqAvg = catsBase.map((s) => {
        if (s==="All") return bankersRound(this.sourceContributions?.all?? null);
        return bankersRound(this.sourceContributions?.sources?.[s] ?? 0);
      })

      // Label formatter
      const labelFormatter = (p) => {
        const v =
          typeof p.value === "number" && isFinite(p.value) ? p.value : null;
        if (v === null) return "";
        // In diff view, just “dB” (still A-weighted conceptually, but it’s a delta)
        const unit = p.seriesName.includes("SEL")
          ? isDiff
            ? "dB"
            : "dB·s"
          : isDiff
            ? "dB"
            : "dBA";
        const val = isDiff
          ? v >= 0
            ? `+${v}`
            : v
          : v
        return `${val} ${unit}`;
      };

      // Build yAxis config (zero-centered for diff if requested)
      let yAxis = {
        type: "value",
        name: isDiff ? "ΔdB" : "dB / dBA",
        splitLine: { show: true },
      };
      if (isDiff && this.zeroCenterDiff) {
        // remove laeq15min and sel from allVals while awaiting Clayton
        // to verify the caluclations for ΔLAeq and ΔSEL
        //const allVals = [...laeqAvg, ...laeq15min, ...sel].filter(
        const allVals = [...laeqAvg,].filter(
          (x) => typeof x === "number" && isFinite(x)
        );
        const maxAbs = allVals.length
          ? Math.max(...allVals.map((x) => Math.abs(x)))
          : 10;
        const pad = Math.ceil(maxAbs + 1);
        yAxis = { ...yAxis, min: -pad, max: pad };
      }

      const commonBarProps = {
        type: "bar",
        label: {
          show: true,
          position: "top",
          distance: 6,
          formatter: labelFormatter,
          fontSize: 11,
        },
        labelLayout: { hideOverlap: true },
      };

      const option = {
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
          formatter: (params) => {
            const name = params[0]?.axisValue ?? "";
            let html = `<b>${name}</b><br/>`;
            params.forEach((p) => {
              const v = typeof p.data === "number" ? p.data : null;
              const unit = p.seriesName.includes("SEL")
                ? isDiff
                  ? "dB"
                  : "dB·s"
                : isDiff
                  ? "dB"
                  : "dBA";
              const val =
                v == null
                  ? "—"
                  : isDiff
                    ? v >= 0
                      ? `+${v.toFixed(1)}`
                      : v.toFixed(1)
                    : v.toFixed(1);
              html += `${p.marker}${p.seriesName}: <b>${val} ${unit}</b><br/>`;
            });
            return html;
          },
        },
        legend: { top: 0, left: "center" },
        grid: { top: 40, left: 10, right: 10, bottom: 10, containLabel: true },
        xAxis: { type: "category", data: catsBase },
        yAxis,
        series: [
          {
            name: isDiff
              ? "ΔLAeq (log avg over selected N)"
              : "LAeq15min (Log Average over 15 minutes)",
            data: laeqAvg,
            barGap: "0%",
            colorBy: "data",
            itemStyle: { color: "#009B69", opacity: 0.6 },
            ...commonBarProps,
          },
          // Disable ΔLAeq and ΔSEL until Clayton has verified the calculations used in them
          /*{
            name: isDiff ? "ΔLAeq (15-min, N=90)" : "LAeq (15-min, N=90)",
            data: laeq15min,
            ...commonBarProps,
          },
          {
            name: isDiff ? "ΔSEL" : "SEL (dB·s)",
            data: sel,
            ...commonBarProps,
          },*/
        ],
      };

      this.chart.setOption(option, true);
    },
  },
  watch: {
    selectedOptions() {
      this.$refs.weighted_spectra_ref?.updateChart?.();
      this.filterSelectedTimestamps();
      this.updateAggregatesChart();
    },
    weightedSpectra: {
      deep: true,
      handler() {
        this.updateAggregatesChart();
      },
    },
    includeMlMode() {
      this.updateAggregatesChart();
    },
    mlMinDb() {
      this.updateAggregatesChart();
    },
    zeroCenterDiff() {
      // re-render axis bounds
      this.updateAggregatesChart();
    },
  },
  mounted() {
    window.scrollTo({ top: 0 });
    this.applyModeToAPIParams();
    this.filterSelectedTimestamps();
    this.fetchSourceContributions();
    this.updateAggregatesChart();
  },
  beforeUnmount() {
    if (this.chart) {
      window.removeEventListener("resize", this.chart.resize);
      this.chart.dispose();
      this.chart = null;
    }
  },
};
</script>

<style scoped>
.factsheet-page-component {
  display: grid;
  grid-template-rows: var(--control-height-lg) 1fr;
  height: 744px;
}

.dashboard {
  display: flex;
  gap: 10px;
  height: 100%;
  padding: 10px;
}

.dashboard h1 {
  margin: 0;
  text-align: center;
}

.dropdown-items {
  height: 40px;
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.filter-status {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
  justify-content: flex-end;
  flex-grow: 1;
  margin-right: var(--spacing-sm);
}

.ml-controls {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 6px;
  visibility: hidden;
}

.ml-radio {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}

.ml-threshold {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
}

.num-input {
  width: 80px;
  padding: 4px 6px;
  border: 1px solid var(--colour-border);
  border-radius: 6px;
}

.pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 8px;
  font-size: 12px;
}

.pill-on {
  background: #e6f2ff;
  color: #095aba;
  border: 1px solid #b5d4ff;
}

.pill-off {
  background: #f6f6f6;
  color: var(--colour-border-strong);
  border: 1px solid var(--colour-border-light);
}

.left-column {
  flex: 1;
  display: grid;
  grid-template-rows: 1fr 1fr;
  height: 100%;
  gap: 10px;
}

.right-column {
  flex: 1;
  display: flex;
  height: 100%;
  flex-direction: column;
  gap: 10px;
}

.component-container {
  flex: 1;
  border: 2px solid var(--colour-border-strong);
  border-radius: 15px;
  padding: 10px;
  background-color: var(--colour-bg-light);
  display: flex;
  flex-direction: column;
}

.component-container.aggregates {
  height: 100%;
}

.aggregates-chart {
  width: 100%;
  height: 100%;
  min-height: 220px;
}

.aggregates-chart-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

.aggregates-spinner-overlay {
  position: absolute;
  inset: 0;
  background: var(--colour-overlay-whiteout);
  display: flex;
  align-items: center;
  justify-content: center;
}

.aggregates-spinner{
  width: 40%;
  height: 40%;
}
</style>
